import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './Dashboard.css';
import './DocumentUpload.css';
import { fetchFirstFnolJson } from '../utils/fnolApi';
import { openDocumentUploadBucket } from '../utils/documentUploadLink';

const isRealClaimId = (claimId) =>
  typeof claimId === 'string' &&
  claimId.trim() &&
  !claimId.includes('$') &&
  !claimId.includes('{') &&
  !claimId.toLowerCase().includes('session.params');

const getClaimIdFromFnol = (record = {}) => {
  if (isRealClaimId(record.claim_id)) return record.claim_id;
  if (typeof record.fnol_id === 'string' && record.fnol_id.startsWith('F-')) {
    return record.fnol_id.replace(/^F-/, 'C-');
  }
  return '';
};

const getFnolIdFromClaim = (claimId = '') =>
  typeof claimId === 'string' && claimId.startsWith('C-')
    ? claimId.replace(/^C-/, 'F-')
    : '';

const isGeneratedFnolId = (fnolId) =>
  typeof fnolId === 'string' &&
  fnolId.trim() &&
  !fnolId.includes('$') &&
  !fnolId.includes('{') &&
  !fnolId.toLowerCase().includes('session.params');

const hiddenClaimIds = new Set(['C-260826-001', 'C-260821-006', 'C-260831-001']);

const isSelectableClaimRecord = (claim = {}) =>
  isRealClaimId(claim.claim_id) &&
  !hiddenClaimIds.has(claim.claim_id) &&
  (
    isGeneratedFnolId(claim.fnol_id) ||
    isGeneratedFnolId(getFnolIdFromClaim(claim.claim_id))
  );

const getClaimSortTimestamp = (claim = {}) => {
  const parsed = Date.parse(
    claim.updated_at ||
      claim.processed_at ||
      claim.created_at ||
      claim.scan_timestamp ||
      claim.date
  );
  return Number.isNaN(parsed) ? 0 : parsed;
};

const isUnavailableName = (value) => {
  const text = String(value || '').trim();
  return !text || /^not available/i.test(text) || /current evidence/i.test(text);
};

const getUsableName = (...values) =>
  values.map((value) => String(value || '').trim()).find((value) => !isUnavailableName(value)) || '';

const getClaimLabel = (claim = {}) => {
  const insured = getUsableName(
    claim.customer_name,
    claim.insured,
    claim.insured_name,
    claim.Insured_Name,
    claim.claimant_name,
    claim.reported_by,
    claim.claimant
  );
  return insured ? `${claim.claim_id} - ${insured}` : claim.claim_id;
};

const getClaimOptionLabel = (claim = {}) =>
  claim.is_manual ? claim.claim_id : getClaimLabel(claim);

const stripExistingClaimPrefix = (fileName = '') =>
  String(fileName || 'document')
    .replace(/^(?:[a-f0-9]{24,40}_)?(?:C-\d{6}-\d{3,}_)+/i, '')
    .replace(/^_+/, '')
    .trim() || 'document';

const buildClaimUploadFile = (file, claimId) => {
  if (!file || !claimId) return file;

  const baseName = stripExistingClaimPrefix(file.name);
  const uploadName = `${claimId}_${baseName}`;
  if (uploadName === file.name) return file;

  return new File([file], uploadName, {
    type: file.type || 'application/octet-stream',
    lastModified: file.lastModified,
  });
};

const buildFnolClaimSummary = (record = {}) => {
  const claimId = getClaimIdFromFnol(record);
  if (!claimId) return null;

  return {
    ...record,
    claim_id: claimId,
    fnol_id: record.fnol_id || getFnolIdFromClaim(claimId),
    customer_name: record.customer_name || record.insured || record.reported_by || '',
  };
};

const mergeUploadClaimRecords = (claimRecords = [], fnolRecords = []) => {
  const claimById = new Map(
    claimRecords
      .filter((claim) => isRealClaimId(claim.claim_id))
      .map((claim) => [claim.claim_id, claim])
  );

  fnolRecords.forEach((record) => {
    const summary = buildFnolClaimSummary(record);
    if (!summary) return;
    claimById.set(summary.claim_id, {
      ...claimById.get(summary.claim_id),
      ...summary,
    });
  });

  return Array.from(claimById.values())
    .filter(isSelectableClaimRecord)
    .sort((firstClaim, secondClaim) => {
      const dateDifference =
        getClaimSortTimestamp(secondClaim) - getClaimSortTimestamp(firstClaim);
      return dateDifference || String(secondClaim.claim_id).localeCompare(firstClaim.claim_id);
    })
    .slice(0, 10);
};

const getHydratedClaimName = (detail = {}) => {
  const formData = detail.form_data || {};
  const claim = detail.claim || {};
  const insights = detail.insights || {};
  const nextBestActions = detail.next_best_actions || {};

  return getUsableName(
    formData.claimant_name,
    formData.claimant,
    formData.insured,
    formData.insured_name,
    formData.reported_by,
    claim.claimant_name,
    claim.insured_full_name,
    claim.customer_name,
    insights.Insured_Name,
    nextBestActions.Insured_Name
  );
};

const DocumentUpload = ({ onLogout }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [claimsError, setClaimsError] = useState('');
  const [selectedClaimId, setSelectedClaimId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [isEditingClaimId, setIsEditingClaimId] = useState(false);
  const [claimIdDraft, setClaimIdDraft] = useState('');
  const [manualClaim, setManualClaim] = useState(null);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    let active = true;

    const loadClaims = async () => {
      setClaimsLoading(true);
      try {
        const [claimResult, fnolResult] = await Promise.all([
          fetchFirstFnolJson('/claim-records'),
          fetchFirstFnolJson('/fnol-records').catch(() => ({ records: [] })),
        ]);
        if (!active) return;

        let nextClaims = mergeUploadClaimRecords(
          claimResult.records || [],
          fnolResult.records || []
        );

        const claimsNeedingNames = nextClaims.filter((claim) =>
          isUnavailableName(claim.customer_name || claim.insured || claim.claimant_name)
        );
        if (claimsNeedingNames.length) {
          const hydratedClaims = await Promise.all(
            claimsNeedingNames.map(async (claim) => {
              try {
                const detail = await fetchFirstFnolJson(
                  `/claim-records/${encodeURIComponent(claim.claim_id)}`
                );
                const hydratedName = getHydratedClaimName(detail);
                return hydratedName
                  ? [claim.claim_id, { ...claim, customer_name: hydratedName }]
                  : [claim.claim_id, claim];
              } catch {
                return [claim.claim_id, claim];
              }
            })
          );
          const hydratedById = new Map(hydratedClaims);
          nextClaims = nextClaims.map((claim) => hydratedById.get(claim.claim_id) || claim);
        }

        setClaims(nextClaims);
        setSelectedClaimId((currentClaimId) => currentClaimId || nextClaims[0]?.claim_id || '');
        setClaimsError('');
      } catch (error) {
        if (active) setClaimsError(error.message || 'Unable to load claims.');
      } finally {
        if (active) setClaimsLoading(false);
      }
    };

    loadClaims();
    return () => {
      active = false;
    };
  }, []);

  const displayFileSize = selectedFile
    ? `${Math.max(selectedFile.size / 1024, 1).toFixed(1)} KB`
    : '';

  const claimOptions =
    manualClaim && !claims.some((claim) => claim.claim_id === manualClaim.claim_id)
      ? [manualClaim, ...claims]
      : claims;

  useEffect(() => {
    setClaimIdDraft(selectedClaimId);
    setEditError('');
  }, [selectedClaimId]);

  const handleEditClaim = () => {
    if (!selectedClaimId) return;
    setIsEditingClaimId(true);
    setClaimIdDraft(selectedClaimId);
    setEditError('');
  };

  const handleSaveClaimId = () => {
    const nextClaimId = claimIdDraft.trim().toUpperCase();
    if (!isRealClaimId(nextClaimId)) {
      setEditError('Please enter a valid claim ID.');
      return;
    }
    if (!claims.some((claim) => claim.claim_id === nextClaimId)) {
      setManualClaim({
        claim_id: nextClaimId,
        fnol_id: getFnolIdFromClaim(nextClaimId),
        is_manual: true,
      });
    } else {
      setManualClaim(null);
    }
    setSelectedClaimId(nextClaimId);
    setIsEditingClaimId(false);
    setEditError('');
  };

  const handleUpload = async (event) => {
    event?.preventDefault();
    if (!selectedClaimId || !selectedFile) return;

    const formData = new FormData();
    formData.append('record_id', selectedClaimId);
    formData.append('file', buildClaimUploadFile(selectedFile, selectedClaimId));

    setIsUploading(true);
    setUploadResult(null);
    setUploadError('');

    try {
      const result = await fetchFirstFnolJson('/documents/upload', {
        method: 'POST',
        body: formData,
        timeoutMs: 180000,
      });
      setUploadResult(result);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      setUploadError(error.message || 'Unable to upload document.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="dashboard-page document-upload-page">
      <Header onLogout={onLogout} />

      <div className="page-body">
        <aside className="sidebar mini-sidebar">
          <nav className="sidebar-nav">
            <button onClick={() => navigate('/home')} className="nav-item">
              <i className="fas fa-home"></i><span className="nav-label">Home</span>
            </button>
            <button onClick={() => navigate('/my-work')} className="nav-item">
              <i className="fas fa-briefcase"></i><span className="nav-label">My Work</span>
            </button>
            <button onClick={() => navigate('/crm')} className="nav-item">
              <i className="fas fa-address-book"></i><span className="nav-label">FNOL</span>
            </button>
            <button onClick={() => navigate('/claims')} className="nav-item">
              <i className="fas fa-file-shield"></i><span className="nav-label">Claim</span>
            </button>
            <button onClick={() => navigate('/ops-dashboard')} className="nav-item">
              <i className="fas fa-tachometer-alt"></i><span className="nav-label">Ops Dashboard</span>
            </button>
            <button onClick={() => navigate('/claims?tab=quality-score')} className="nav-item quality-score-nav-item">
              <i className="fas fa-clipboard-check"></i><span className="nav-label">QA</span>
            </button>
            <button onClick={() => navigate('/agent-dashboard')} className="nav-item">
              <i className="fas fa-gauge-high"></i><span className="nav-label">AI Agent Dashboard</span>
            </button>
            <button onClick={openDocumentUploadBucket} className="nav-item active">
              <i className="fas fa-file-arrow-up"></i><span className="nav-label">Document Upload</span>
            </button>
          </nav>
          <div className="sidebar-footer">
            <button onClick={onLogout} className="nav-item logout-btn">
              <i className="fas fa-sign-out-alt"></i><span className="nav-label">Logout</span>
            </button>
          </div>
        </aside>

        <main className="db-content document-upload-content">
          <div className="document-upload-motion-bg" aria-hidden="true">
            <span className="document-upload-orbit document-upload-orbit-pdf">PDF</span>
            <span className="document-upload-orbit document-upload-orbit-docx">DOCX</span>
            <span className="document-upload-orbit document-upload-orbit-img">IMG</span>
            <span className="document-upload-orbit document-upload-orbit-mail">
              <i className="fas fa-envelope"></i>
            </span>
          </div>

          <section className="document-upload-panel" aria-label="Document upload">
            <header className="document-upload-header">
              <div>
                <span className="document-upload-kicker">Claim documents</span>
                <h1>Document Upload</h1>
              </div>
              <div className="document-upload-status">
                <span>{claimsLoading ? 'Loading' : `${claimOptions.length} claims`}</span>
              </div>
            </header>

            <div className="document-upload-form">
              <div className="document-upload-grid">
                <label className="document-upload-field">
                  <span>Claim</span>
                  <select
                    value={selectedClaimId}
                    onChange={(event) => {
                      setSelectedClaimId(event.target.value);
                      setIsEditingClaimId(false);
                      setUploadResult(null);
                      setUploadError('');
                    }}
                    disabled={claimsLoading || !claimOptions.length}
                  >
                    {claimsLoading && <option value="">Loading claims...</option>}
                    {!claimsLoading && !claimOptions.length && <option value="">No claims available</option>}
                    {claimOptions.map((claim) => (
                      <option value={claim.claim_id} key={claim.claim_id}>
                        {getClaimOptionLabel(claim)}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  type="button"
                  className="document-upload-edit"
                  disabled={!selectedClaimId}
                  onClick={handleEditClaim}
                >
                  <i className="fas fa-pen"></i>
                  <span>Edit</span>
                </button>
              </div>

              {isEditingClaimId && (
                <div className="document-upload-edit-panel">
                  <div className="document-upload-edit-header">
                    <div>
                      <span>{selectedClaimId}</span>
                      <strong>Edit claim ID</strong>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsEditingClaimId(false)}
                      aria-label="Close edit panel"
                    >
                      <i className="fas fa-xmark"></i>
                    </button>
                  </div>

                  <label className="document-upload-claim-id-edit">
                    <span>Claim ID</span>
                    <input
                      value={claimIdDraft}
                      onChange={(event) => setClaimIdDraft(event.target.value)}
                      placeholder="C-260825-008"
                    />
                  </label>

                  <div className="document-upload-edit-actions">
                    <button type="button" onClick={handleSaveClaimId}>
                      <i className="fas fa-check"></i>
                      <span>Apply</span>
                    </button>
                    <button type="button" onClick={() => setIsEditingClaimId(false)}>
                      Cancel
                    </button>
                  </div>

                  {editError && <div className="document-upload-message error">{editError}</div>}
                </div>
              )}

              <label className="document-upload-dropzone">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.heic,.mp4,.mov,.avi,.mkv,.eml,.msg,image/*,video/*"
                  onChange={(event) => {
                    setSelectedFile(event.target.files?.[0] || null);
                    setUploadResult(null);
                    setUploadError('');
                  }}
                />
                <span className="document-upload-drop-icon">
                  <i className="fas fa-file-arrow-up"></i>
                </span>
                <span className="document-upload-drop-title">
                  {selectedFile ? selectedFile.name : 'Choose document'}
                </span>
                <span className="document-upload-drop-meta">
                  {selectedFile ? displayFileSize : 'PDF, DOCX, images, videos, or email files'}
                </span>
              </label>

              <button
                type="button"
                className="document-upload-submit"
                onClick={handleUpload}
                disabled={isUploading || !selectedClaimId || !selectedFile}
              >
                <i className={isUploading ? 'fas fa-circle-notch fa-spin' : 'fas fa-upload'}></i>
                <span>{isUploading ? 'Uploading...' : 'Upload'}</span>
              </button>
            </div>

            {claimsError && <div className="document-upload-message error">{claimsError}</div>}
            {uploadError && <div className="document-upload-message error">{uploadError}</div>}
            {uploadResult && (
              <div className="document-upload-message success">
                <strong>Uploaded</strong>
                <span>{uploadResult.name}</span>
                <span>{uploadResult.gcs_uri || uploadResult.object_path}</span>
              </div>
            )}
          </section>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default DocumentUpload;
